import React, { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import confetti from 'canvas-confetti';
import { toast } from 'react-toastify';
import { ReceivingTradeItem } from '../ActiveReceivingPage';
import { AssetUrls } from 'config/assets';
import './FirstTradeCelebrationModal.css';

/**
 * Strict image sourcing — ONLY from trade items, never ambient state.
 * Priority: scryfallId direct URL > item image as-is > card back placeholder.
 */
const getCardImageUrl = (card: ReceivingTradeItem): string => {
  // Priority 1: Construct art_crop URL directly from scryfallId (most reliable)
  if (card.scryfallId) {
    return `https://cards.scryfall.io/art_crop/front/${card.scryfallId[0]}/${card.scryfallId[1]}/${card.scryfallId}.jpg`;
  }
  // Priority 2: Use the image URL from trade items as-is (no CDN transformation)
  if (card.image) {
    return card.image;
  }
  // Priority 3: Card back placeholder — never show a different card
  return AssetUrls.cards.defaultBack;
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  shareImageUrl: string | null;
  receivedCards: ReceivingTradeItem[];
}

const FirstTradeCelebrationModal: React.FC<Props> = ({
  isOpen,
  onClose,
  shareImageUrl,
  receivedCards,
}) => {
  const triggerConfetti = useCallback(() => {
    if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    const colors = ['#6F51E4', '#E02CDC', '#a78bfa', '#e879f9', '#fbbf24', '#34d399', '#f472b6'];

    confetti({
      particleCount: 80,
      spread: 70,
      origin: { x: 0.25, y: 0.5 },
      colors,
      disableForReducedMotion: true,
    });
    confetti({
      particleCount: 80,
      spread: 70,
      origin: { x: 0.75, y: 0.5 },
      colors,
      disableForReducedMotion: true,
    });
  }, []);

  useEffect(() => {
    if (isOpen) triggerConfetti();
  }, [isOpen, triggerConfetti]);

  // Diagnostic logging for missing image data
  useEffect(() => {
    if (isOpen && receivedCards.length > 0) {
      const missingData = receivedCards.filter(c => !c.scryfallId && !c.image);
      if (missingData.length > 0) {
        console.warn('[FirstTradeCelebration] Cards missing both scryfallId and image:',
          missingData.map(c => c.name));
      }
    }
  }, [isOpen, receivedCards]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleShare = async () => {
    const imageUrl = shareImageUrl;
    if (!imageUrl) {
      toast.error('Share image not available yet');
      return;
    }

    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();

      // Use Web Share API on mobile if available
      if (navigator.share && /Mobi|Android/i.test(navigator.userAgent)) {
        const file = new File([blob], 'my-first-trade.png', { type: 'image/png' });
        await navigator.share({
          files: [file],
          text: 'Just completed my first trade on TradeMagic! 🎴 trademagic.gg',
        });
      } else {
        // Desktop: copy to clipboard
        await navigator.clipboard.write([
          new ClipboardItem({ 'image/png': blob }),
        ]);
        toast.success('Copied to clipboard — ready to paste!');
      }
    } catch (err: any) {
      // User cancelled share dialog is not an error
      if (err?.name !== 'AbortError') {
        toast.error('Failed to share image');
      }
    }
  };

  // Get up to 3 card images for the CSS fallback fan
  const displayCards = receivedCards.slice(0, 3);
  const cardRotations = displayCards.length === 1
    ? [0]
    : displayCards.length === 2
      ? [-6, 6]
      : [-12, 0, 12];
  const cardTranslations = displayCards.length === 1
    ? [0]
    : displayCards.length === 2
      ? [-40, 40]
      : [-70, 0, 70];

  const cardFan = (
    <div className="first-trade-celebration-card-fan">
      {displayCards.map((card, i) => (
        <div
          key={i}
          className="first-trade-celebration-card"
          style={{
            transform: `rotate(${cardRotations[i]}deg) translateX(${cardTranslations[i]}px)`,
            zIndex: i === 1 ? 2 : 1,
          }}
        >
          <img
            src={getCardImageUrl(card)}
            alt={card.name}
            onError={(e) => {
              const target = e.currentTarget;
              if (!target.dataset.fallback) {
                target.dataset.fallback = 'true';
                target.src = AssetUrls.cards.defaultBack;
              }
            }}
          />
        </div>
      ))}
    </div>
  );

  return createPortal(
    <div
      className="first-trade-celebration-backdrop"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="first-trade-celebration-title"
    >
      <div className="first-trade-celebration-modal">
        {/* Share image region */}
        <div className="first-trade-celebration-image-region">
          <div className="first-trade-celebration-gradient" />

          {cardFan}
        </div>

        {/* Modal chrome */}
        <div className="first-trade-celebration-content">
          <p id="first-trade-celebration-title" className="first-trade-celebration-title">
            First trade complete 🎴
          </p>
          <p className="first-trade-celebration-subtitle">
            Congratulations on your first trade! You're officially a TradeMagic Trader.
          </p>
          <div className="first-trade-celebration-actions">
            <button
              className="first-trade-celebration-btn-share"
              onClick={handleShare}
            >
              Share
            </button>
            <button
              className="first-trade-celebration-btn-continue"
              onClick={onClose}
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default FirstTradeCelebrationModal;
